import { useCallback, useState } from 'react'

export enum FileDropzoneRejectReason {
  InvalidSize = 'InvalidSize',
  InvalidType = 'InvalidType',
}

export interface DropzoneOptions {
  maxSize?: number
  accept?: string[]
  onFiles?: (file?: File) => void
  onFilesReject?: (file: File, reason: FileDropzoneRejectReason) => void
}

export const useFileDropzone = ({ maxSize, accept = [], onFiles, onFilesReject }: DropzoneOptions) => {
  const [isDragging, setIsDragging] = useState(false)

  const getValidationResult = useCallback(
    (file: File) => {
      if (maxSize) {
        const validSize = file.size <= maxSize
        if (!validSize) {
          return { file, rejectReason: FileDropzoneRejectReason.InvalidSize }
        }
      }

      const validType = accept.length === 0 || accept.includes('.' + file.name.split('.').pop() || '') //check by extension. A deeper validation will be done by server
      if (!validType) {
        return { file, rejectReason: FileDropzoneRejectReason.InvalidType }
      }

      return { file }
    },
    [accept, maxSize],
  )

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      setIsDragging(false)

      const files = Array.from(event.dataTransfer.files)
      if (files.length === 0) {
        return
      }
      const { file, rejectReason } = getValidationResult(files[0])

      if (!rejectReason) {
        onFiles?.(file)
        return
      }

      onFilesReject?.(file, rejectReason)
    },
    [getValidationResult, onFiles, onFilesReject],
  )

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target
    if (!files || files.length === 0) return

    const { file, rejectReason } = getValidationResult(files[0])
    if (!rejectReason) {
      onFiles?.(file)
      return
    }

    event.target.value = ''
    onFilesReject?.(file, rejectReason)
  }

  return {
    isDragging,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleInput,
  }
}
